import { Show, onMount, lazy, useContext, Switch, Match, createSignal } from 'solid-js';
import { Route, Routes } from '@solidjs/router';
import { GlobalStyles } from './global.styles';
import { StyledAppRoot, StyledAppInner, StyledPdfFactoryRoot, StyledChatbotTeaserButtonFlexRow, StyledFakeChatbotBubble } from './app.styles';
import { PageContainer } from '../components/page/page';
import { HeaderContainer } from '../components/header/header';
import { FooterContainer } from '../components/footer/footer';
import { AppContext } from '../app-context-provider/app-context-provider';
import { Title } from '@solidjs/meta';
import { LoginMenu } from '../components/login-menu/login-menu';
import { ErrorCatcher } from '../tools/error-catcher';
import { Announcement } from '../components/announcement/announcement';
import { LocaleController } from '../components/language-selector/locale-controller';
import { NativeAppPrompt } from '../components/native-app-prompt/native-app-prompt';
import { NewsletterSignupPromptForPdf } from '../components/pdf-builder/pdf-dashboard/pdf-newsletter-signup-prompt/pdf-newsletter-signup-prompt';
import { FooterAtosCareContainer } from '../components/footer/atos-care/footer-atos-care';
import { HeaderAtosCareContainer } from '../components/header/atos-care/header-atos-care';
import { BasketContextProvider } from '../components/basket/basket-context-provider';
import { Menu } from '../components/menu/menu';
import { HeaderMedicalHcpContainer } from '../components/header/medical-hcp/hcp-header';
import theme from '../style/theme';
import { HeaderCampaignContainer } from '../components/header/campaign-header/campaign-header';
import { UserProfileMenu } from '../components/user-profile-menu/user-profile-menu';
import { getChatbotLoader } from '../tools/chatbots/chatbot-loader';
import { SmallCornerDialog } from '../components/small-corner-dialog/small-corner-dialog';
import { StyledVerticalSpace } from '../ui-components/utility-style-components/spacing';
import { Button } from '../ui-components/button/button';
import { Text } from '../ui-components/text/text';

// Lazy load everything else
const Product = lazy(() => import('../components/product/product').then(imported => ({ default: imported.Product })));
const Event = lazy(() => import('../components/event/event').then(imported => ({ default: imported.Event })));
const BlogNews = lazy(() => import('../components/blog-news/blog-news').then(imported => ({ default: imported.BlogNews })));
const Video = lazy(() => import('../components/video/video').then(imported => ({ default: imported.Video })));
const Course = lazy(() => import('../components/course/course').then(imported => ({ default: imported.Course })));
const Search = lazy(() => import('../components/search/search').then(imported => ({ default: imported.Search })));
const PdfFactory = lazy(() => import('../components/pdf-builder/pdf-factory/pdf-factory').then(imported => ({ default: imported.PdfFactory })));
const CertificateFactory = lazy(() => import('../components/certificate-factory/certificate-factory').then(imported => ({ default: imported.CertificateFactory })));

const chatbotSupportedSites = [ 'de-ch', 'fr-ch', 'fr', 'en-us', 'en-au' ];

export const App = () => {
    const { 
        productsEntityName,
        eventsEntityName,
        blogAndNewsEntityName,
        videosEntityName,
        coursesEntityName,
        searchPage,
        userState,
        isPdfGenerator,
        isCertificateGenerator,
        siteInfo,
        appStore,
        viewport,
        headerSettings,
        siteDomains,
        deploymentEnvironment,
        localize,
    } = useContext(AppContext);

    const [ chatbotLoaded, setChatbotLoaded ] = createSignal(false);
    const [ consentGivenState, setConsentGivenState ] = createSignal(false);
    const [ isChatbotEnabledSite ] = createSignal(chatbotSupportedSites.includes(siteInfo.siteLanguage || '') && siteInfo.siteType !== 'atos-care');
    const [ showChatbotCookieConsentDialog, setShowChatbotCookieConsentDialog ] = createSignal(false);

    onMount(() => {
        handleChatbotLoading();
    });

    const handleChatbotLoading = () => {
        if (!isChatbotEnabledSite()) {
            return;
        }

        checkConsentForChatbot();

        // Listen for changes in consent
        window.addEventListener('CookiebotOnAccept', () => {
            checkConsentForChatbot();
        });
    };
    
    const checkConsentForChatbot = () => {
        if (window?.Cookiebot?.consent?.marketing && window?.Cookiebot?.consent?.statistics) {
            setConsentGivenState(true);

            if (!chatbotLoaded()) {
                const scriptLoader = getChatbotLoader(siteInfo.target, siteInfo.siteId, siteDomains, deploymentEnvironment);
                
                if (scriptLoader) {
                    scriptLoader();
                    setChatbotLoaded(true);
                }
            }
        } else {
            // Consent previously given, but now revoked
            if (consentGivenState()) {
                setConsentGivenState(false);
                window.location.reload();
            }
        }
    };

    const handleOnClickCookieConsentToggle = (e: any) => {
        e.preventDefault();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.CookieConsent?.renew();
    };

    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;

    const showHcpHeader = () => headerSettings.activateHcpHeader && appStore.isHcp() && !isOriginalTablet();

    const FakeChatbotIcon = () => (
        <StyledFakeChatbotBubble
            onClick={() => setShowChatbotCookieConsentDialog(true)}
            aria-label={localize('chat-with-a-representative', 'Chat with a representative')}
        >
            <svg
                aria-label={localize('chat-icon', 'Chat icon')}
                id="embeddedMessagingIconChat"
                class="embeddedMessagingIconChat"
                viewBox="0 0 100 95.22"
                width={'3rem'}
                height={'3rem'}
            >
                <path d="M50 0c27.614 0 50 20.52 50 45.833S77.614 91.667 50 91.667c-8.458 0-16.425-1.925-23.409-5.323-13.33 6.973-21.083 9.839-23.258 8.595-2.064-1.18.114-8.436 6.534-21.767C3.667 65.54 0 56.08 0 45.833 0 20.52 22.386 0 50 0zm4.583 61.667H22.917a2.917 2.917 0 000 5.833h31.666a2.917 2.917 0 000-5.833zm12.5-15.834H22.917a2.917 2.917 0 000 5.834h44.166a2.917 2.917 0 000-5.834zM79.583 30H22.917a2.917 2.917 0 000 5.833h56.666a2.917 2.917 0 000-5.833z"></path>
            </svg>
        </StyledFakeChatbotBubble>
    );

    const ChatbotCookieWarning = () => (
        <SmallCornerDialog>
            <Text
                fontSize='small'
                displayRedVerticalLine={false}
                align='center'
                fontStyle='bold'
                noBlockSpacing={true}
            >
                {localize('chatbot-teaser-heading', 'Chatbot available')}
            </Text>
            <StyledVerticalSpace size={1} />
            <Text
                fontSize='small'
                displayRedVerticalLine={false}
                align='center'
            >
                {localize('chatbot-teaser-text', 'To use the chatbot you must consent to our marketing and statistics policies')}
            </Text>
            <StyledChatbotTeaserButtonFlexRow>
                <Button
                    label={localize('chatbot-teaser-open-cookie-consent-button-text', 'Open cookie consent')}
                    onClick={handleOnClickCookieConsentToggle}
                    variant='primary'
                    small={true}
                    customCss='white-space: nowrap;'
                />
                <Button
                    label={localize('chatbot-teaser-dismiss-button-text', 'Dismiss')}
                    onClick={() => setShowChatbotCookieConsentDialog(false)}
                    variant='secondary'
                    small={true}
                    customCss='white-space: nowrap;'
                />
            </StyledChatbotTeaserButtonFlexRow>
        </SmallCornerDialog>
    );

    const ChatbotPlaceholder = () => (
        <Show when={!consentGivenState()}>
            <Show when={isChatbotEnabledSite()}>
                <FakeChatbotIcon/>
            </Show>

            <Show when={showChatbotCookieConsentDialog()}>
                <ChatbotCookieWarning />
            </Show>
        </Show>
    );

    return (
        <>
            <Show when={siteInfo?.siteType === 'atos-care'} fallback={<Title>Atos Medical</Title>}>
                <Title>Atos Care</Title>
            </Show>

            <GlobalStyles />
            <StyledAppRoot>
                <Announcement />

                <LocaleController />


                <NativeAppPrompt />
                <NewsletterSignupPromptForPdf />

                <BasketContextProvider siteInfo={siteInfo} userState={userState}>

                    <Switch>
                        <Match when={siteInfo?.siteType === 'atos-care'}>
                            <HeaderAtosCareContainer isHcp={appStore.isHcp()} />
                        </Match>
                        <Match when={appStore.isCampaignPage()}>
                            <HeaderCampaignContainer/>
                        </Match>
                        <Match when={siteInfo?.siteType === 'default' && !showHcpHeader()}>
                            <HeaderContainer />
                        </Match>
                        <Match when={siteInfo?.siteType === 'default' && showHcpHeader()}>
                            <HeaderMedicalHcpContainer />
                        </Match>
                    </Switch>

                    <Show when={userState.user} fallback={(
                        <LoginMenu />
                    )}>
                        <UserProfileMenu user={userState.user} />
                    </Show>

                    <Menu />

                    <StyledPdfFactoryRoot id="pdf-factory-root" />

                    <ChatbotPlaceholder />

                    <ErrorCatcher componentName="App">
                        <StyledAppInner id="app">
                            <Show when={isPdfGenerator}>
                                <PdfFactory />
                            </Show>

                            <Show when={isCertificateGenerator}>
                                <CertificateFactory />
                            </Show>

                            <Show when={!isPdfGenerator && !isCertificateGenerator}>
                                <Routes>
                                    {/*
                                    NOTE: When adding new routes here -> If you want to render a menu on the
                                    route and its subroute components, you need to render the menu component inside 
                                    a portal leading back here. We need context from the page/post to be able to 
                                    get the correct menu from the db. Hence this annoying setup.

                                    NOTE 2: We only add these dynamic routes if there's a value for the entity, otherwise 
                                    it will act as a "catch all"-route and catch stuff that we normally want our PageContainer to handle.

                                    NOTE 3: Dont forget to add menu portal when creating new routes!
                                    */}
                                    {productsEntityName && <Route path={`/${productsEntityName}/:productSlug`} element={<Product />} />}
                                    {eventsEntityName && <Route path={`/${eventsEntityName}/:eventSlug`} element={<Event />} />}
                                    {blogAndNewsEntityName && <Route path={`/${blogAndNewsEntityName}/:postSlug`} element={<BlogNews />} />}
                                    {videosEntityName && <Route path={`/${videosEntityName}/:videoSlug`} element={<Video />} />}
                                    {coursesEntityName && <Route path={`/${coursesEntityName}/:courseId`} element={<Course />} />}
                                    {searchPage?.url !== '' && <Route path={`${searchPage?.url}`} element={<Search />} />}
                                    {/* {shop.basketPage?.url !== '' && <Route path={`${shop.basketPage?.url}`} element={<Basket />} />} */}
                                    <Route path="/*" element={<PageContainer />} />
                                </Routes>
                            </Show>
                        </StyledAppInner>
                    </ErrorCatcher>
                </BasketContextProvider>
                <Show when={siteInfo?.siteType === 'default'} fallback={<FooterAtosCareContainer />}>
                    <FooterContainer />
                </Show>
            </StyledAppRoot>
        </>
    );
};
